import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, MagnifyingGlass, X } from "@phosphor-icons/react";
import { Button, Input, Label } from "@repo/ui";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { PermissionGate } from "@/components/security";
import { PatientTable } from "@/components/patients/patient-table";
import { PatientDetailDrawer } from "@/components/patients/patient-detail-drawer";
import { PatientManagementModal } from "@/components/forms/patient-management-modal";
import { DateInput } from "@/components/forms";
import { getPatients } from "@/lib/api/patients";
import type { Patient } from "@/lib/types/patients";

const PAGE_SIZE = 25;

export function PatientsPage() {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dobFilter, setDobFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Modal state — null means closed, undefined means create, Patient means edit
  const [modalPatient, setModalPatient] = useState<Patient | null | undefined>(
    null
  );
  const isModalOpen = modalPatient !== null;

  // Debounce search — reset page on new search
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const fetchPatients = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getPatients({
        search: debouncedSearch || undefined,
        dob: dobFilter || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setPatients(res.data.patients);
      setTotal(res.data.total);
    } catch {
      setPatients([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, dobFilter, page]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // ---- Pagination ----
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  // ---- Drawer ----

  const handleViewPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setDrawerOpen(true);
  };

  const handleDrawerClose = (open: boolean) => {
    if (!open) {
      setSelectedPatient(null);
    }
    setDrawerOpen(open);
  };

  // ---- Modal ----

  const handleOpenCreate = () => {
    setModalPatient(undefined); // undefined = create mode
  };

  const handleOpenEdit = (patient: Patient) => {
    setModalPatient(patient);
  };

  /** Also callable from the drawer's Edit button */
  const handleEditFromDrawer = (patient: Patient) => {
    setDrawerOpen(false);
    setModalPatient(patient);
  };

  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      setModalPatient(null);
    }
  };

  const handleModalSaved = () => {
    handleRefresh();
  };

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Patients</h1>
          <p className="text-sm text-muted-foreground">
            Manage patient profiles and view linked records
          </p>
        </div>
        <PermissionGate permission="patients:create">
          <Button
            size="sm"
            className="gap-1.5 h-9"
            onClick={handleOpenCreate}
            data-testid="patient-create"
          >
            <Plus weight="bold" size={15} />
            New Patient
          </Button>
        </PermissionGate>
      </div>

      <div className="flex items-end gap-3">
        <div className="relative max-w-sm flex-1">
          <MagnifyingGlass
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            size={16}
          />
          <Input
            placeholder="Search patients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="patient-search-input"
          />
        </div>
        <div className="relative">
          <div className="flex items-center gap-1.5">
            <Label
              htmlFor="dob-filter"
              className="text-xs font-medium text-muted-foreground whitespace-nowrap"
            >
              DOB
            </Label>
            <DateInput
              id="dob-filter"
              placeholder="DOB"
              value={dobFilter}
              onChange={(v) => {
                setDobFilter(v);
                setPage(1);
              }}
              max={new Date().toISOString().split("T")[0]}
              className="w-[160px]"
            />
            {dobFilter && (
              <button
                type="button"
                onClick={() => {
                  setDobFilter("");
                  setPage(1);
                }}
                className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear date of birth filter"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <PatientTable
            patients={patients}
            onView={handleViewPatient}
            onEdit={handleOpenEdit}
            onDeleted={handleRefresh}
          />

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Showing {rangeStart}–{rangeEnd} of {total} patient
                {total !== 1 ? "s" : ""}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!canPrev}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 tabular-nums">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!canNext}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <PatientDetailDrawer
        patient={selectedPatient}
        open={drawerOpen}
        onOpenChange={handleDrawerClose}
        onEdit={handleEditFromDrawer}
        onDeleted={handleRefresh}
      />

      <PatientManagementModal
        open={isModalOpen}
        onOpenChange={handleModalOpenChange}
        patient={isModalOpen ? modalPatient : null}
        onPatientsChange={handleModalSaved}
      />
    </div>
  );
}
